import { NavLink, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Home, 
  Utensils, 
  Dumbbell, 
  TrendingUp, 
  Trophy,
  Users,
  MoreHorizontal
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

export const BottomNavigation = () => {
  const location = useLocation();
  const [sheetOpen, setSheetOpen] = useState(false);
  const { t } = useTranslation();

  const NAV_ITEMS = [
    { path: "/dashboard", icon: Home, label: t("nav.home") },
    { path: "/nutrition", icon: Utensils, label: t("nav.nutrition") },
    { path: "/workouts", icon: Dumbbell, label: t("nav.workouts") },
    { path: "/progress", icon: TrendingUp, label: t("nav.progress") },
  ];

  const MORE_ITEMS = [
    { path: "/ranking", icon: Trophy, label: t("nav.ranking") },
    { path: "/social", icon: Users, label: t("nav.social") },
    { path: "/report", icon: () => <span className="text-lg">🏆</span>, label: t("nav.report") },
    { path: "/macros", icon: () => <span className="text-lg">📊</span>, label: t("nav.macros") },
    { path: "/analytics", icon: () => <span className="text-lg">📈</span>, label: t("nav.analytics") },
    { path: "/help", icon: () => <span className="text-lg">❓</span>, label: t("nav.help") },
  ];
  
  const isMoreActive = MORE_ITEMS.some(item => location.pathname === item.path);

  return (
    <motion.nav
      initial={{ y: 100 }}
      animate={{ y: 0 }}
      transition={{ type: "spring", stiffness: 300, damping: 30 }}
      className="fixed bottom-0 left-0 right-0 z-50 md:hidden"
    >
      <div className="absolute inset-0 glass-strong border-t border-border/50" />
      
      <div className="relative flex items-center justify-around h-16 pb-safe px-2">
        {NAV_ITEMS.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;

          return (
            <NavLink
              key={item.path}
              to={item.path}
              className="relative flex flex-col items-center justify-center flex-1 h-full tap-highlight"
            >
              {isActive && (
                <motion.div
                  layoutId="bottomNavPill"
                  className="absolute inset-x-3 top-1.5 bottom-1.5 rounded-lg bg-primary/10 border border-primary/20"
                  transition={{ type: "spring", stiffness: 380, damping: 32 }}
                />
              )}
              <motion.div
                whileTap={{ scale: 0.92 }}
                className={cn(
                  "relative flex flex-col items-center gap-1 transition-colors duration-200",
                  isActive ? "text-primary" : "text-muted-foreground"
                )}
              >
                <Icon className="w-[18px] h-[18px]" strokeWidth={isActive ? 2 : 1.5} />
                <span className={cn(
                  "text-[10px] tracking-wide transition-all duration-200",
                  isActive ? "font-semibold text-primary" : "font-medium text-muted-foreground"
                )}>
                  {item.label}
                </span>
              </motion.div>
            </NavLink>
          );
        })}

        <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
          <SheetTrigger asChild>
            <button className="relative flex flex-col items-center justify-center flex-1 h-full tap-highlight">
              {isMoreActive && (
                <motion.div
                  layoutId="bottomNavPill"
                  className="absolute inset-x-3 top-1.5 bottom-1.5 rounded-lg bg-primary/10 border border-primary/20"
                  transition={{ type: "spring", stiffness: 380, damping: 32 }}
                />
              )}
              <motion.div
                whileTap={{ scale: 0.92 }}
                className={cn(
                  "relative flex flex-col items-center gap-1 transition-colors duration-200",
                  isMoreActive ? "text-primary" : "text-muted-foreground"
                )}
              >
                <MoreHorizontal className="w-[18px] h-[18px]" strokeWidth={isMoreActive ? 2 : 1.5} />
                <span className={cn(
                  "text-[10px] tracking-wide transition-all duration-200",
                  isMoreActive ? "font-semibold text-primary" : "font-medium text-muted-foreground"
                )}>
                  {t("nav.more")}
                </span>
              </motion.div>
            </button>
          </SheetTrigger>
          <SheetContent side="bottom" className="rounded-t-3xl pb-safe">
            <SheetHeader>
              <SheetTitle className="text-left">{t("nav.moreOptions")}</SheetTitle>
            </SheetHeader>
            <div className="grid grid-cols-4 gap-4 py-6">
              {MORE_ITEMS.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                
                return (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setSheetOpen(false)}
                    className="flex flex-col items-center gap-2 p-3 rounded-xl tap-highlight hover:bg-muted/50 transition-colors"
                  >
                    <div className={cn(
                      "w-12 h-12 rounded-xl flex items-center justify-center transition-colors",
                      isActive 
                        ? "bg-primary/20 text-primary" 
                        : "bg-muted text-muted-foreground"
                    )}>
                      <Icon className="w-6 h-6" />
                    </div>
                    <span className={cn(
                      "text-xs font-medium",
                      isActive ? "text-primary" : "text-foreground"
                    )}>
                      {item.label}
                    </span>
                  </NavLink>
                );
              })}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </motion.nav>
  );
};
